import { useState } from "react";
import { ChevronRight } from "lucide-react";

import type { FlagResolution } from "@/application/merchants/debug_context_repository";

import {
  categoryStyle,
  effectiveVariant,
  flagCategory,
  flagLabel,
  isExpandable,
  variantKind,
  variantLabel,
  variantStyle,
} from "./flag-display";

function FlagRow({
  flag,
  deviceReportedAt,
}: {
  flag: FlagResolution;
  deviceReportedAt: string | null;
}) {
  const [open, setOpen] = useState(false);

  const category = flagCategory(flag.key);
  const shown = effectiveVariant(flag);
  const kind = variantKind(shown);
  const expandable = isExpandable(kind, flag.deviceVariant);

  const row = (
    <div className="flex min-w-0 items-center gap-2 py-1">
      {expandable ? (
        <ChevronRight
          className={`text-subtle-foreground h-3 w-3 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      ) : (
        <span className="w-3 shrink-0" />
      )}

      <span
        className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${categoryStyle(category)}`}
      >
        {category}
      </span>

      <span
        className="text-foreground min-w-0 flex-1 truncate text-left text-sm"
        title={flag.key}
      >
        {flagLabel(flag.key)}
      </span>

      <span
        className={`max-w-[8rem] shrink-0 truncate rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${variantStyle(kind)}`}
      >
        {variantLabel(shown, kind)}
      </span>
    </div>
  );

  if (!expandable) return <li>{row}</li>;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full cursor-pointer rounded focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
      >
        {row}
      </button>

      {open && (
        <dl className="border-border mb-1 ml-5 space-y-1 border-l pl-3 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Variant</dt>
            <dd className="text-foreground font-mono break-all">
              {flag.serverVariant ?? "–"}
            </dd>
          </div>
          {flag.serverPayload && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Payload</dt>
              <dd className="text-foreground font-mono break-all">
                {flag.serverPayload}
              </dd>
            </div>
          )}
          {flag.deviceVariant && (
            <div className="flex justify-between gap-3">
              <dt className="text-amber-700">Device</dt>
              <dd className="font-mono break-all text-amber-800">
                {flag.deviceVariant}
                {deviceReportedAt
                  ? ` · ${new Date(deviceReportedAt).toLocaleDateString()}`
                  : ""}
              </dd>
            </div>
          )}
        </dl>
      )}
    </li>
  );
}

/**
 * This merchant's flag resolution, one line per flag.
 *
 * Grouped by category so the eye lands on the family it needs — the release
 * flags gating v2 are a different question from the ops flags gating a
 * force-upgrade. Flags whose variant carries a payload, and any where the
 * device disagrees with the server, expand to show the values.
 */
export function FlagList({
  flags,
  deviceReportedAt,
}: {
  flags: FlagResolution[];
  deviceReportedAt: string | null;
}) {
  const sorted = [...flags].sort((a, b) => {
    const categoryOrder = flagCategory(a.key).localeCompare(
      flagCategory(b.key),
    );
    return categoryOrder !== 0 ? categoryOrder : a.key.localeCompare(b.key);
  });

  return (
    <ul className="divide-border divide-y">
      {sorted.map((flag) => (
        <FlagRow
          key={flag.key}
          flag={flag}
          deviceReportedAt={deviceReportedAt}
        />
      ))}
    </ul>
  );
}
